import React from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import selectAnalytics from './selectAnalytics';
import ExternalVideo from '../Product/ExternalVideo';
import Price from '../Product/Price';
import LazyImage from '../../hocs/Lazy/LazyImage';
import { addToBag } from '../../actions/checkout';
import { searchInput } from '../../actions/environment';
import { imageSizeTranslation } from '../../utils/image';
import { translate, translateObjectData, prefix } from '../../utils/locale';
import { urlifyAllowAccents } from '../../utils/string';
import s from './ProductItem.scss';

const emptyArray = [];

const ProductItem = ({ variantClasses = emptyArray, imageColumn, content, setup, currentPath, currentHash, currentView, canonical, searchInputText, itemIndex = 1, productCollectionSwatches, ...props }) => {
	content = translateObjectData(setup, content);
	const path = canonical ? translate(setup, content, 'canonicalUri') : `${currentPath}/${content.uri}${currentHash}`;
	const relatedSwatches = {};
	let productItemClasses = [s.productItem];
	if (variantClasses) productItemClasses = [...productItemClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('productItem') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];
	const perRow = parseInt(variantClasses.find((className) => className && /^productItem__perRow_\d+$/.test(className))?.split('_').pop(), 10);
	let imageWidth = '3';
	if (imageColumn) imageWidth = imageColumn;
	else if (perRow > 0) imageWidth = Math.ceil(12 / perRow);

	if (content.relatedVariants && productCollectionSwatches) {
		content.relatedVariants.forEach((productKey) => {
			if (!productKey || !productCollectionSwatches[productKey]) return;
			Object.keys(productCollectionSwatches[productKey]).forEach((v) => {
				if (!relatedSwatches[v]) relatedSwatches[v] = productCollectionSwatches[productKey][v];
			});
		});
	}

	const clearSearch = () => {
		if (searchInputText !== '') props.searchInput('');
		// Clear queue in listing to get all batched impressions.
		selectAnalytics([content], setup, currentView, itemIndex);
	};

	let price = content.price;

	if (content.priceRange) {
		// We'll use the minimum price here for PLP's
		price = Object.keys(content.priceRange).reduce((acc, key) => ({ ...acc, [key]: { price: content.priceRange[key].price.min } }), {});
	}

	const [isHovered, setIsHovered] = React.useState(false);
	const validMedia = content.media.filter((item) => item && item.src);
	const hasMultipleImages = validMedia.length > 1;

	return (
		<div className={productItemClasses.join(' ')}>
			<Link className={s.productItem_link} to={prefix(setup, path)} onClick={clearSearch}>
				<div className={s.productItem_images} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} >
					{
						validMedia.length > 0 && validMedia.map((item, index) => {
							const isFirst = index === 0;
							const isLast = index === validMedia.length - 1;
							let ariaHidden;
							if (hasMultipleImages) {
								if (isFirst && !isLast) ariaHidden = isHovered;
								else if (isLast && !isFirst) ariaHidden = !isHovered;
								else ariaHidden = true;
							}
							return (
								<figure key={item.src} className={s.productItem_image} aria-hidden={ariaHidden || undefined}>
									{ item.type === 'IMAGE' && <LazyImage {...imageSizeTranslation(imageWidth, { ...item, alt: item.alt || `${content.name}${content.variant ? ` - ${content.variant}` : ''}` }, '_')} /> }
									{ item.type === 'EXTERNAL_VIDEO' && <ExternalVideo url={item.src} uid={urlifyAllowAccents(item.alt)} itemProp="thumbnail" /> }
								</figure>
							);
						})
					}
				</div>
				<div className={[s.productItem_text, !content.inStock ? s.productItem_text__soldOut : null].join(' ')}>
					{/* eslint-disable-next-line react/no-array-index-key */}
					<span className={s.productItem_text_name}>{ content.name.split('®').map((part, i, arr) => (i < arr.length - 1 ? [part, <span key={i} className="u_registered">®</span>] : part)) }</span>
					{ content?.variations && <span className={s.productItem_text_variant}>{ Object.keys(content.variations).map((v) => content.variations[v]).join(', ') }</span> }
					<div className={s.productItem_text_price}>
						<Price price={price} setup={setup} soldOut={!content.inStock ? translate(setup, 'soldOut') : false} />
					</div>
					<div className={s.productItem_text_swatches}>
						{
							Object.keys(relatedSwatches).length > 0 && Object.keys(relatedSwatches).sort((a, b) => (relatedSwatches[a].order > relatedSwatches[b].order ? 1 : -1)).slice(0, 6).map((swatch, i) => {
								if (i > 5) return <div key={relatedSwatches[swatch].key} className={[s.productItem_text_swatch, s.productItem_text_swatch__more].join(' ')} />;
								return (
									<div key={relatedSwatches[swatch].key} className={s.productItem_text_swatch}>
										{ !relatedSwatches[swatch].image && relatedSwatches[swatch].color && <span style={{ backgroundColor: relatedSwatches[swatch].color }} /> }
										{ relatedSwatches[swatch].image && <LazyImage {...imageSizeTranslation('1', relatedSwatches[swatch].image, '-', true, false, 200)} /> }
									</div>
								);
							})
						}
					</div>
				</div>
			</Link>
		</div>
	);
};

const mapState = ({ environment, productCache }, props) => ({
	setup: environment.setup,
	searchInputText: environment.searchInputText,
	productCollectionSwatches: productCache?.[environment.setup.market]?.[environment.setup.pricelist]?.productCollectionSwatches,
	...props,
});

export default hot(module)(connect(mapState, { searchInput, addToBag })(ProductItem));
